import {
  BIOME_DISPLAY,
  biomeForLevel,
  Biome,
  type Food,
  FoodKind,
  type GameEvent,
  type Obstacle,
  type Particle,
  type Popup,
  RunStatus,
  SteerDir,
  type World,
} from './types';
import {
  ARGB_COMBO,
  ARGB_FOOD_GOLDEN,
  ARGB_WHITE,
  argbForBiome,
  obstacleColorArgb,
} from './colors';
import { Difficulty, DIFFICULTY_PARAMS } from '../../data/settings';
import { mulberry32 } from './colors';
import { spawnObstacleForBiome } from './spawn';
import { collidesWithSnake } from './collision';

const TAU = Math.PI * 2;
const HEAD_RADIUS = 18;
const MAX_COMBO = 5;

export interface CollisionResult {
  snakeLength: number;
  survivingObstacles: Obstacle[];
  shake: number;
  shakeMag: number;
  gameOver: boolean;
  gameOverWorld: World | null;
  hitObstacle: boolean;
}

interface FoodResult {
  snakeLength: number;
  food: Food[];
  score: number;
  particles: Particle[];
  popups: Popup[];
  combo: number;
  kind: FoodKind;
}

interface TimersResult {
  message: string | null;
  messageSeconds: number;
  rush: number;
  calm: number;
}

export function headFracForLength(length: number): number {
  const lengthBoost = Math.max(0, length - 6);
  return Math.max(0.2, Math.min(0.85, 0.85 - lengthBoost * 0.012));
}

export const HEAD_FRAC_LERP_ALPHA = 0.18;

export class VerticalEngine {
  readonly width: number;
  private rng: () => number;
  private startLength: number;

  private maxHeadSpeed = 1300;
  private headAccel = 3400;
  private headFriction = 4.0;
  private segmentSpacing = 30;

  private baseScrollSpeed = 220;
  private scrollGrowth = 26;
  private spawnDensity = 1.0;

  private timeSinceLevelStart = 0;
  private nextLevelAt = 9;
  private lastSpawnY = 0;
  private currentBiome: Biome = Biome.MEADOW;
  private biomeMessage: string | null = null;
  private biomeMessageSeconds = 0;
  private nextRushIn = 9;
  private rushSeconds = 0;
  private nextCalmIn = 18;
  private calmSeconds = 0;
  private runSeed = 0;
  private patternStep = 0;
  private slowMoSeconds = 0;

  private steerInput: SteerDir = SteerDir.NONE;

  constructor(width = 720, rngSeed = Math.floor(Math.random() * 1e9), startLength = 6) {
    this.width = width;
    this.rng = mulberry32(rngSeed);
    this.startLength = startLength;
  }

  newGame(
    highScore = 0,
    longestRun = 0,
    furthestLevel = 1,
    difficulty: Difficulty = Difficulty.NORMAL,
    seed: number = this.rng() * 1000,
  ): World {
    const params = DIFFICULTY_PARAMS[difficulty];
    this.baseScrollSpeed = params.baseScrollSpeed;
    this.scrollGrowth = params.scrollGrowth;
    this.spawnDensity = params.spawnDensity;

    this.timeSinceLevelStart = 0;
    this.nextLevelAt = 9;
    this.currentBiome = Biome.MEADOW;
    this.biomeMessage = `Entering ${BIOME_DISPLAY[Biome.MEADOW]}`;
    this.biomeMessageSeconds = 2.4;
    this.nextRushIn = 9;
    this.rushSeconds = 0;
    this.nextCalmIn = 18;
    this.calmSeconds = 1.6;
    this.runSeed = seed;
    this.patternStep = 0;
    this.slowMoSeconds = 0;

    const headX = this.width / 2;
    const headY = 0;
    const body: Array<[number, number]> = [];
    for (let i = 0; i < this.startLength; i++) {
      body.push([headX, headY - i * this.segmentSpacing]);
    }

    const seededObstacles: Obstacle[] = [];
    const startSpawns = [500, 850, 1200, 1600, 2050];
    for (const y of startSpawns) {
      seededObstacles.push(
        spawnObstacleForBiome(this.width, Biome.MEADOW, 1, y, this.patternStep, this.rng),
      );
      this.patternStep += 1;
    }
    this.lastSpawnY = startSpawns[startSpawns.length - 1] ?? 0;

    const initialFood = this.spawnInitialFood(body);

    return {
      width: this.width,
      scrollY: 0,
      scrollSpeed: this.baseScrollSpeed,
      level: 1,
      levelProgress: 0,
      timeToNextLevel: this.nextLevelAt,
      snakeLength: this.startLength,
      peakSnakeLength: this.startLength,
      snakeBody: body,
      snakeVx: 0,
      snakeMaxSpeed: this.maxHeadSpeed,
      food: initialFood,
      obstacles: seededObstacles,
      particles: [],
      popups: [],
      score: 0,
      highScore,
      longestRun,
      furthestLevel,
      status: RunStatus.RUNNING,
      shakeSeconds: 0,
      shakeMagnitude: 0,
      biome: Biome.MEADOW,
      biomeMessage: this.biomeMessage,
      biomeMessageSeconds: this.biomeMessageSeconds,
      rushSeconds: 0,
      calmSeconds: this.calmSeconds,
      runSeed: this.runSeed,
      levelUpCounter: 0,
      slowMoSeconds: 0,
      difficulty,
      headFrac: headFracForLength(this.startLength),
      combo: 0,
      invincibilitySeconds: 0,
      milestoneIndex: 0,
    };
  }

  private spawnInitialFood(body: ReadonlyArray<readonly [number, number]>): Food[] {
    const occupiedX = new Set<number>();
    for (const [x] of body) {
      occupiedX.add(Math.round(x / 30));
    }
    const out: Food[] = [];
    for (let i = 0; i < 8; i++) {
      let x: number;
      let attempts = 0;
      do {
        x = 60 + this.rng() * (this.width - 120);
        attempts++;
      } while (occupiedX.has(Math.round(x / 30)) && attempts < 20);
      const kindRoll = this.rng();
      const kind = kindRoll < 0.06 ? FoodKind.GOLDEN : FoodKind.NORMAL;
      out.push({
        x,
        y: 150 + Math.floor(this.rng() * (900 - 150)),
        seed: this.rng() * 100,
        kind,
      });
    }
    return out;
  }

  setSteer(dir: SteerDir): void {
    this.steerInput = dir;
  }

  pause(world: World): World {
    return world.status === RunStatus.RUNNING ? { ...world, status: RunStatus.PAUSED } : world;
  }

  resume(world: World): World {
    return world.status === RunStatus.PAUSED ? { ...world, status: RunStatus.RUNNING } : world;
  }

  step(world: World, dt: number, cheatInvincible = false): { world: World; events: GameEvent[] } {
    if (world.status !== RunStatus.RUNNING) return { world, events: [] };

    const events: GameEvent[] = [];
    const dtClamped = Math.min(dt, 1 / 30);

    const rushMul = this.rushSeconds > 0 ? 1.5 : 1;
    const slowMoMul = this.slowMoSeconds > 0 ? 0.5 : 1;
    const invincible = world.invincibilitySeconds > 0 || cheatInvincible;
    const baseScroll =
      (this.baseScrollSpeed + (world.level - 1) * this.scrollGrowth) * rushMul * slowMoMul;

    const newScrollY = world.scrollY + baseScroll * dtClamped;

    const newTime = this.timeSinceLevelStart + dtClamped;
    this.timeSinceLevelStart = newTime;
    const inCalm = this.calmSeconds > 0 || world.calmSeconds > 0;

    const head = world.snakeBody[0]!;
    const [newVx, newHeadX] = this.applySteerToHead(world, head[0], dtClamped);
    const newHeadY = newScrollY;

    const newBody = this.buildBodyChain(world, newHeadX, newHeadY);
    const particles: Particle[] = this.carryParticles(world.particles, baseScroll, dtClamped);
    const popups: Popup[] = [...world.popups];

    for (let i = popups.length - 1; i >= 0; i--) {
      popups[i] = { ...popups[i], life: popups[i].life - dtClamped };
      if (popups[i].life <= 0) popups.splice(i, 1);
    }

    const foodResult = this.resolveFood(
      world,
      newHeadX,
      newHeadY,
      newScrollY,
      baseScroll,
      dtClamped,
      particles,
      popups,
    );

    const coll = this.resolveObstacles(
      world,
      newHeadX,
      newHeadY,
      newScrollY,
      dtClamped,
      foodResult.snakeLength,
      foodResult.food,
      foodResult.score,
      particles,
      popups,
      invincible,
      baseScroll,
    );

    if (coll.gameOver) {
      const score = foodResult.score;
      const newRecord = score > world.highScore;
      events.push({ type: 'GAME_OVER', score, newHighScore: newRecord, snakeLength: world.peakSnakeLength });
      return { world: { ...coll.gameOverWorld!, peakSnakeLength: world.peakSnakeLength }, events };
    }

    let newShake = coll.shake;
    let newShakeMag = coll.shakeMag;

    this.addTrailParticles(particles, newBody, this.currentBiome, false);

    const newObstacles = this.spawnObstacles(
      world,
      newScrollY,
      this.currentBiome,
      inCalm,
      coll.survivingObstacles,
    );

    const prevLevel = world.level;
    const [newLevel, levelProgress, outTimeToNext] = this.advanceLevel(world, newTime);
    const leveledUp = newLevel > prevLevel;
    let levelUpCounter = world.levelUpCounter;
    let newSlowMo = this.slowMoSeconds;
    if (leveledUp) {
      levelUpCounter += 1;
      newSlowMo = 0.6;
      const flashColor = argbForBiome(biomeForLevel(newLevel));
      for (let i = 0; i < 50; i++) {
        const a = (i / 50) * TAU;
        const speed = 200 + (i % 3) * 80;
        particles.push({
          x: newHeadX,
          y: newHeadY,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed * 0.6,
          life: 1.1,
          maxLife: 1.1,
          color: flashColor,
          radius: 10,
        });
      }
      newShake = 0.45;
      newShakeMag = 22;
      events.push({
        type: 'LEVEL_UP',
        newLevel,
        biome: biomeForLevel(newLevel),
        newScrollSpeed: baseScroll,
      });
    }
    if (newSlowMo > 0) {
      newSlowMo = Math.max(0, newSlowMo - dtClamped);
    }
    this.slowMoSeconds = newSlowMo;

    const timers = this.updateTimersAndBiome(
      world,
      newLevel,
      dtClamped,
      newHeadX,
      newHeadY,
      particles,
    );
    const [outShake, outShakeMag] = this.decayShake(newShake, newShakeMag, dtClamped);

    const targetHeadFrac = headFracForLength(coll.snakeLength);
    const frames = dtClamped * 60;
    const factor = 1 - Math.pow(1 - HEAD_FRAC_LERP_ALPHA, frames);
    const newHeadFrac = world.headFrac + (targetHeadFrac - world.headFrac) * factor;

    let newCombo = foodResult.combo;
    let newInvincibility = world.invincibilitySeconds;
    let newMilestoneIndex = world.milestoneIndex;

    if (coll.hitObstacle && coll.snakeLength < foodResult.snakeLength) {
      newCombo = Math.max(0, newCombo - 1);
    }

    if (newInvincibility > 0) {
      newInvincibility = Math.max(0, newInvincibility - dtClamped);
    }

    const milestoneThresholds = [100, 250, 500, 1000, 2000, 5000, 10000];
    while (newMilestoneIndex < milestoneThresholds.length && foodResult.score >= milestoneThresholds[newMilestoneIndex]) {
      newMilestoneIndex++;
      popups.push({
        x: newHeadX,
        y: newHeadY - 60,
        text: `SCORE ${milestoneThresholds[newMilestoneIndex - 1]!}!`,
        life: 2.0,
        maxLife: 2.0,
        color: 0xff00ffcc,
      });
      for (let i = 0; i < 20; i++) {
        const a = this.rng() * TAU;
        const s = 100 + this.rng() * 150;
        particles.push({
          x: newHeadX,
          y: newHeadY,
          vx: Math.cos(a) * s,
          vy: Math.sin(a) * s,
          life: 0.8,
          maxLife: 0.8,
          color: 0xff00ffcc,
          radius: 4 + this.rng() * 4,
        });
      }
    }

    const finalWorld: World = {
      ...world,
      scrollY: newScrollY,
      scrollSpeed: baseScroll,
      level: newLevel,
      levelProgress,
      timeToNextLevel: outTimeToNext,
      snakeLength: coll.snakeLength,
      peakSnakeLength: Math.max(world.peakSnakeLength, coll.snakeLength),
      snakeBody: this.trimBody(newBody, coll.snakeLength),
      snakeVx: newVx,
      food: foodResult.food,
      obstacles: newObstacles,
      particles,
      popups,
      score: foodResult.score,
      shakeSeconds: outShake,
      shakeMagnitude: outShakeMag,
      biome: this.currentBiome,
      biomeMessage: timers.message,
      biomeMessageSeconds: timers.messageSeconds,
      rushSeconds: timers.rush,
      calmSeconds: timers.calm,
      levelUpCounter,
      slowMoSeconds: newSlowMo,
      headFrac: newHeadFrac,
      combo: newCombo,
      invincibilitySeconds: newInvincibility,
      milestoneIndex: newMilestoneIndex,
    };

    if (foodResult.snakeLength > world.snakeLength) {
      const kind = foodResult.kind ?? FoodKind.NORMAL;
      const comboMultiplier = Math.min(MAX_COMBO, foodResult.combo);
      events.push({ type: 'ATE_FOOD', kind, combo: comboMultiplier, scoreGain: foodResult.score - world.score });
    } else if (finalWorld.snakeLength < world.snakeLength) {
      events.push({ type: 'TURNED' });
    }

    const milestone = Math.floor(finalWorld.score / 50);
    const prevMilestone = Math.floor(world.score / 50);
    if (milestone > prevMilestone && milestone > 0) {
      events.push({ type: 'SCORE_MILESTONE', score: finalWorld.score });
    }

    return { world: finalWorld, events };
  }

  private applySteerToHead(world: World, headX: number, dt: number): [number, number] {
    let newVx = world.snakeVx;
    const accelMul = 1;
    if (this.steerInput === SteerDir.LEFT) newVx -= this.headAccel * accelMul * dt;
    else if (this.steerInput === SteerDir.RIGHT) newVx += this.headAccel * accelMul * dt;
    else {
      const decay = Math.exp(-this.headFriction * dt);
      newVx *= decay;
      if (Math.abs(newVx) < 4) newVx = 0;
    }
    newVx = Math.max(-this.maxHeadSpeed, Math.min(this.maxHeadSpeed, newVx));
    let newHeadX = headX + newVx * dt;
    const margin = 24;
    if (newHeadX < margin) {
      newHeadX = margin;
      newVx = 0;
    }
    if (newHeadX > world.width - margin) {
      newHeadX = world.width - margin;
      newVx = 0;
    }
    return [newVx, newHeadX];
  }

  private buildBodyChain(
    world: World,
    newHeadX: number,
    newHeadY: number,
  ): Array<[number, number]> {
    const out: Array<[number, number]> = [[newHeadX, newHeadY]];
    let prevX = newHeadX;
    let prevY = newHeadY;
    const len = world.snakeBody.length;
    for (let i = 1; i < len; i++) {
      const px = world.snakeBody[i]![0];
      const py = world.snakeBody[i]![1];
      const ddx = prevX - px;
      const ddy = prevY - py;
      const dist = Math.sqrt(ddx * ddx + ddy * ddy);
      if (dist < 0.0001) {
        out.push([prevX, prevY - this.segmentSpacing]);
      } else {
        out.push([prevX - (ddx * this.segmentSpacing) / dist, prevY - (ddy * this.segmentSpacing) / dist]);
      }
      prevX = out[out.length - 1]![0];
      prevY = out[out.length - 1]![1];
    }
    return out;
  }

  private carryParticles(existing: ReadonlyArray<Particle>, baseScroll: number, dt: number): Particle[] {
    const out: Particle[] = [];
    for (const p of existing) {
      const age = p.life - dt;
      if (age <= 0) continue;
      out.push({
        x: p.x,
        y: p.y + baseScroll * dt,
        vx: p.vx,
        vy: p.vy,
        life: age,
        maxLife: p.maxLife,
        color: p.color,
        radius: p.radius,
      });
    }
    return out;
  }

  private resolveFood(
    world: World,
    newHeadX: number,
    newHeadY: number,
    newScrollY: number,
    scrollSpeed: number,
    _dt: number,
    particles: Particle[],
    popups: Popup[],
  ): FoodResult {
    const scale = scrollSpeed / this.baseScrollSpeed;
    const visibleBehind = 800 * scale;
    let snake = world.snakeLength;
    let score = world.score;
    let combo = world.combo;
    let eatenKind: FoodKind = FoodKind.NORMAL;
    const food: Food[] = [];
    for (const f of world.food) {
      const delta = newScrollY - f.y;
      if (delta > visibleBehind || delta < -2200) continue;
      const fx = f.x;
      const distSq = (newHeadX - fx) ** 2 + (newHeadY - f.y) ** 2;
      const eatRadius = HEAD_RADIUS + 18;
      if (distSq < eatRadius * eatRadius) {
        combo = Math.min(MAX_COMBO, combo + 1);
        const multiplier = combo;
        let points = 0;
        let segments = 0;
        if (f.kind === FoodKind.GOLDEN) {
          points = 50 * multiplier;
          segments = 2;
        } else {
          points = 10 * multiplier;
          segments = 1;
        }
        snake += segments;
        score += points;
        eatenKind = f.kind;

        if (f.kind === FoodKind.GOLDEN) {
          popups.push({
            x: newHeadX,
            y: newHeadY - 30,
            text: `+${points}`,
            life: 1.0,
            maxLife: 1.0,
            color: ARGB_FOOD_GOLDEN,
          });
        } else if (multiplier > 1) {
          popups.push({
            x: newHeadX,
            y: newHeadY - 30,
            text: `+${points} x${multiplier}`,
            life: 1.0,
            maxLife: 1.0,
            color: ARGB_COMBO,
          });
        }

        const pColor = f.kind === FoodKind.GOLDEN ? ARGB_FOOD_GOLDEN : ARGB_WHITE;
        for (let i = 0; i < 16; i++) {
          const a = this.rng() * TAU;
          const s = 80 + this.rng() * 100;
          particles.push({
            x: newHeadX,
            y: newHeadY,
            vx: Math.cos(a) * s,
            vy: Math.sin(a) * s,
            life: 0.45,
            maxLife: 0.45,
            color: pColor,
            radius: 5,
          });
        }
        continue;
      }
      food.push(f);
    }
    while (food.length < 6) {
      const kindRoll = this.rng();
      const kind = kindRoll < 0.05 ? FoodKind.GOLDEN : FoodKind.NORMAL;
      food.push({
        x: 60 + this.rng() * (world.width - 120),
        y: newScrollY + 800 + this.rng() * 600,
        seed: this.rng() * 100,
        kind,
      });
    }
    return { snakeLength: snake, food, score, particles, popups, combo, kind: eatenKind };
  }

  private resolveObstacles(
    world: World,
    newHeadX: number,
    newHeadY: number,
    newScrollY: number,
    _dt: number,
    snakeAfterFood: number,
    foodAfter: Food[],
    scoreAfter: number,
    particles: Particle[],
    popups: Popup[],
    invincible: boolean,
    scrollSpeed: number,
  ): CollisionResult {
    let snake = snakeAfterFood;
    let shake = 0;
    let shakeMag = 0;
    const out: Obstacle[] = [];
    let hitObstacle = false;
    const snakeLengthAtDeath = snakeAfterFood;
    for (const o of world.obstacles) {
      if (o.type === 'COMET') {
        const updatedComet = { ...o, x: o.x + o.velocityX * _dt };
        const cullLeft = updatedComet.velocityX < 0 && updatedComet.x < -50;
        const cullRight = updatedComet.velocityX > 0 && updatedComet.x > this.width + 50;
        if (cullLeft || cullRight) {
          continue;
        }
        const collision = invincible ? false : collidesWithSnake(updatedComet, newHeadX, newHeadY, newScrollY);
        if (collision) {
          hitObstacle = true;
          if (snake <= o.damage) {
            const burstColor = obstacleColorArgb(updatedComet);
            for (let i = 0; i < 28; i++) {
              const a = this.rng() * TAU;
              const s = 120 + this.rng() * 200;
              particles.push({
                x: newHeadX,
                y: newHeadY,
                vx: Math.cos(a) * s,
                vy: Math.sin(a) * s,
                life: 1.2,
                maxLife: 1.2,
                color: burstColor,
                radius: 9,
              });
            }
            const go: World = {
              ...world,
              scrollY: newScrollY,
              snakeLength: snake,
              snakeBody: [[newHeadX, newHeadY]],
              snakeVx: 0,
              food: foodAfter,
              obstacles: world.obstacles,
              particles,
              popups,
              score: scoreAfter,
              status: RunStatus.GAME_OVER,
              shakeSeconds: 0.6,
              shakeMagnitude: 28,
              biome: this.currentBiome,
              biomeMessage: this.biomeMessage,
              biomeMessageSeconds: this.biomeMessageSeconds,
              rushSeconds: 0,
              calmSeconds: 0,
              combo: 0,
              invincibilitySeconds: 0,
            };
            return {
              snakeLength: snakeLengthAtDeath,
              survivingObstacles: [...world.obstacles],
              shake: 0,
              shakeMag: 0,
              gameOver: true,
              gameOverWorld: go,
              hitObstacle: true,
            };
          }
          snake = Math.max(1, snake - o.damage);
          shake = 0.22;
          shakeMag = 18;
          const burstColor = obstacleColorArgb(updatedComet);
          for (let i = 0; i < 16; i++) {
            const a = this.rng() * TAU;
            const s = 90 + this.rng() * 140;
            particles.push({
              x: newHeadX,
              y: newHeadY,
              vx: Math.cos(a) * s,
              vy: Math.sin(a) * s,
              life: 0.55,
              maxLife: 0.55,
              color: burstColor,
              radius: 7,
            });
          }
          if (this.stillOnScreen(updatedComet, newScrollY, 2200, scrollSpeed)) {
            out.push(updatedComet);
          }
          continue;
        }
        if (this.stillOnScreen(updatedComet, newScrollY, 2200, scrollSpeed)) {
          out.push(updatedComet);
        }
        continue;
      }
      const collision = invincible ? false : collidesWithSnake(o, newHeadX, newHeadY, newScrollY);
      if (collision) {
        hitObstacle = true;
        if (snake <= o.damage) {
          const burstColor = obstacleColorArgb(o);
          for (let i = 0; i < 28; i++) {
            const a = this.rng() * TAU;
            const s = 120 + this.rng() * 200;
            particles.push({
              x: newHeadX,
              y: newHeadY,
              vx: Math.cos(a) * s,
              vy: Math.sin(a) * s,
              life: 1.2,
              maxLife: 1.2,
              color: burstColor,
              radius: 9,
            });
          }
          const go: World = {
            ...world,
            scrollY: newScrollY,
            snakeLength: snake,
            snakeBody: [[newHeadX, newHeadY]],
            snakeVx: 0,
            food: foodAfter,
            obstacles: world.obstacles,
            particles,
            popups,
            score: scoreAfter,
            status: RunStatus.GAME_OVER,
            shakeSeconds: 0.6,
            shakeMagnitude: 28,
            biome: this.currentBiome,
            biomeMessage: this.biomeMessage,
            biomeMessageSeconds: this.biomeMessageSeconds,
            rushSeconds: 0,
            calmSeconds: 0,
            combo: 0,
            invincibilitySeconds: 0,
          };
          return {
            snakeLength: snakeLengthAtDeath,
            survivingObstacles: [...world.obstacles],
            shake: 0,
            shakeMag: 0,
            gameOver: true,
            gameOverWorld: go,
            hitObstacle: true,
          };
        }
        snake = Math.max(1, snake - o.damage);
        shake = 0.22;
        shakeMag = 18;
        const burstColor = obstacleColorArgb(o);
        for (let i = 0; i < 16; i++) {
          const a = this.rng() * TAU;
          const s = 90 + this.rng() * 140;
          particles.push({
            x: newHeadX,
            y: newHeadY,
            vx: Math.cos(a) * s,
            vy: Math.sin(a) * s,
            life: 0.55,
            maxLife: 0.55,
            color: burstColor,
            radius: 7,
          });
        }
        continue;
      }

      if (this.stillOnScreen(o, newScrollY, 2200, scrollSpeed)) {
        out.push(o);
      }
    }
    return {
      snakeLength: snake,
      survivingObstacles: out,
      shake,
      shakeMag,
      gameOver: false,
      gameOverWorld: null,
      hitObstacle,
    };
  }

  private addTrailParticles(
    particles: Particle[],
    body: ReadonlyArray<readonly [number, number]>,
    biome: Biome,
    isDashing: boolean,
  ): void {
    const color = argbForBiome(biome);
    const count = isDashing ? body.length : Math.floor(body.length / 2);
    for (let i = 0; i < count; i++) {
      if (i === 0) continue;
      if (!isDashing && i % 2 !== 0) continue;
      if (!isDashing && this.rng() < 0.5) continue;
      const seg = body[i]!;
      particles.push({
        x: seg[0],
        y: seg[1],
        vx: isDashing ? (this.rng() - 0.5) * 60 : 0,
        vy: isDashing ? 40 + this.rng() * 80 : 0,
        life: isDashing ? 0.2 : 0.35,
        maxLife: isDashing ? 0.2 : 0.35,
        color,
        radius: isDashing ? 2 + this.rng() * 3 : 4,
      });
    }
  }

  private spawnObstacles(
    world: World,
    newScrollY: number,
    biome: Biome,
    inCalm: boolean,
    surviving: Obstacle[],
  ): Obstacle[] {
    const baseGap = (() => {
      switch (biome) {
        case Biome.MEADOW: return 360;
        case Biome.REEF: return 320;
        case Biome.LATTICE: return 290;
        case Biome.MAELSTROM: return 260;
      }
    })();
    const spawnGap = Math.max(220, baseGap / this.spawnDensity - world.level * 3);
    const scrollDelta = newScrollY - this.lastSpawnY;
    const toSpawn = inCalm ? 0 : Math.min(2, Math.floor(scrollDelta / spawnGap));
    const out: Obstacle[] = [...surviving];
    if (toSpawn > 0 && out.length < 20) {
      const effectiveLevel = Math.min(20, world.level + (this.rushSeconds > 0 ? 1 : 0));
      const n = Math.min(20 - out.length, toSpawn);
      for (let i = 0; i < n; i++) {
        const y = newScrollY + 1700;
        out.push(
          spawnObstacleForBiome(this.width, biome, effectiveLevel, y, this.patternStep, this.rng),
        );
        this.patternStep += 1;
      }
      this.lastSpawnY = newScrollY;
    }
    if (inCalm) this.lastSpawnY = newScrollY;
    return out;
  }

  private advanceLevel(world: World, newTime: number): [number, number, number] {
    if (newTime >= this.nextLevelAt) {
      const newLevel = world.level + 1;
      this.timeSinceLevelStart = 0;
      const nextBiome = biomeForLevel(newLevel);
      const baseDurations: Record<Biome, number> = {
        [Biome.MEADOW]: 12,
        [Biome.REEF]: 11,
        [Biome.LATTICE]: 15,
        [Biome.MAELSTROM]: 22,
      };
      const baseDur = baseDurations[nextBiome] ?? 12;
      this.nextLevelAt = Math.max(8, baseDur - newLevel * 0.05);
      return [newLevel, 0, this.nextLevelAt];
    }
    return [world.level, Math.min(1, newTime / this.nextLevelAt), this.nextLevelAt];
  }

  private updateTimersAndBiome(
    _world: World,
    newLevel: number,
    dt: number,
    hx: number,
    hy: number,
    particles: Particle[],
  ): TimersResult {
    const newBiome = biomeForLevel(newLevel);
    if (newBiome !== this.currentBiome) {
      this.currentBiome = newBiome;
      this.biomeMessage = `Entering ${BIOME_DISPLAY[newBiome]}`;
      this.biomeMessageSeconds = 2.4;
      this.calmSeconds = Math.max(this.calmSeconds, 2.4);
      this.nextRushIn = 9;
      this.nextCalmIn = 22;
      const flashColor = argbForBiome(newBiome);
      for (let i = 0; i < 40; i++) {
        const a = this.rng() * TAU;
        const s = 120 + this.rng() * 160;
        particles.push({
          x: hx,
          y: hy,
          vx: Math.cos(a) * s,
          vy: Math.sin(a) * s * 0.4,
          life: 0.9,
          maxLife: 0.9,
          color: flashColor,
          radius: 7,
        });
      }
    }

    if (this.biomeMessageSeconds > 0) {
      this.biomeMessageSeconds = Math.max(0, this.biomeMessageSeconds - dt);
      if (this.biomeMessageSeconds <= 0) this.biomeMessage = null;
    }

    if (this.currentBiome !== Biome.MEADOW) {
      this.nextRushIn -= dt;
      if (this.nextRushIn <= 0 && this.rushSeconds <= 0) {
        this.rushSeconds = 1.5;
        this.nextRushIn = this.currentBiome === Biome.MAELSTROM ? 7 : 9;
      }
    }
    if (this.rushSeconds > 0) this.rushSeconds = Math.max(0, this.rushSeconds - dt);

    this.nextCalmIn -= dt;
    if (this.nextCalmIn <= 0 && this.calmSeconds <= 0) {
      this.calmSeconds = 2.5;
      this.nextCalmIn = 22;
    }
    if (this.calmSeconds > 0) this.calmSeconds = Math.max(0, this.calmSeconds - dt);

    return {
      message: this.biomeMessage,
      messageSeconds: this.biomeMessageSeconds,
      rush: this.rushSeconds,
      calm: this.calmSeconds,
    };
  }

  private decayShake(shake: number, mag: number, dt: number): [number, number] {
    if (shake <= 0) return [0, 0];
    const s = shake - dt;
    return s <= 0 ? [0, 0] : [s, mag];
  }

  private trimBody(body: ReadonlyArray<[number, number]>, length: number): Array<[number, number]> {
    if (body.length >= length) {
      return body.slice(0, length).map((p) => [p[0], p[1]] as [number, number]);
    }
    const grown: Array<[number, number]> = body.map((p) => [p[0], p[1]] as [number, number]);
    const tail = body[body.length - 1]!;
    for (let i = 0; i < length - body.length; i++) grown.push([tail[0], tail[1]]);
    return grown;
  }

  private stillOnScreen(o: Obstacle, scrollY: number, viewAhead: number, scrollSpeed: number): boolean {
    const delta = scrollY - o.y;
    const scale = scrollSpeed / this.baseScrollSpeed;
    const visibleAhead = 800 * scale;
    return delta > -viewAhead && delta < visibleAhead;
  }
}
